/**
 * Textes du lot L5 — administration et exécution (français, langue de référence). Branché sous la
 * clé `admin` de `fr.ts` ; `admin.en.ts` porte exactement les mêmes clés (typage + `i18n.test.ts`).
 */
export const adminFr = {
  common: {
    identifier: 'Identifiant',
    description: 'Description',
    created: 'Créé',
    updated: 'Mis à jour',
    labels: 'Étiquettes',
    locked: 'Verrouillé',
    yes: 'Oui',
    no: 'Non',
    saved: 'Modifications enregistrées.',
    accessTitle: 'Accès',
    accessEmpty: 'Personne ne porte encore de rôle sur cette ressource.',
    resourceNameHelp: 'Minuscules, chiffres et tirets : ce nom finit dans un nom Kubernetes.',
    countryHelp: 'Code pays ISO à deux lettres (FR, BE…).',
    errors: {
      resourceName: 'Minuscules, chiffres et tirets seulement, 63 caractères au plus.',
      country: 'Deux lettres majuscules, comme FR.',
    },
  },
  statuses: {
    running: 'En service',
    starting: 'Démarrage',
    pending: 'En attente',
    queued: 'En file',
    stopped: 'Arrêté',
    failed: 'En échec',
    succeeded: 'Réussi',
    available: 'Disponible',
    draft: 'Brouillon',
    unknown: 'Inconnu',
  },
  meter: {
    warning: '{{percent}} % — proche de la limite',
    danger: '{{percent}} % — limite presque atteinte',
    valueText: '{{current}} sur {{limit}} ({{percent}} %)',
  },
  notifications: {
    title: 'Activité',
    subtitle: 'Ce que les runs, les workflows et les agents vous ont signalé.',
    retention: 'Le serveur garde les 10 dernières, en mémoire.',
    searchPlaceholder: 'Rechercher une notification…',
    unread: 'non lue',
    markAllRead_one: 'Marquer {{count}} comme lue',
    markAllRead_other: 'Marquer les {{count}} comme lues',
    columns: { kind: 'Nature', title: 'Notification', actor: 'Agent', at: 'Reçue' },
    kinds: { agent: 'Agent', success: 'Réussite', failure: 'Échec', warning: 'Alerte' },
    empty: {
      title: 'Aucune notification',
      description: 'Les fins de run, les échecs et les actions des agents s’affichent ici.',
    },
  },
  usage: {
    resourceCost: {
      title: 'Coût sur les {{count}} derniers jours',
      empty: 'Aucun coût mesuré sur les {{count}} derniers jours.',
    },
    title: 'Usage et coûts',
    subtitle: 'Ce que le tenant a consommé et ce que cela coûte, par période.',
    /** ADR 0089 : la règle de calcul, dite là où s'affichent des minutes et des montants. */
    billingRule: 'Facturé à la minute entamée, du démarrage effectif à la fin.',
    seePricing: 'Voir les tarifs',
    period: 'Période',
    periods: { days_one: 'Dernier jour', days_other: '{{count}} derniers jours' },
    total: 'Total de la période',
    unavailable: 'indisponible',
    byProject: 'Coûts par projet',
    byProjectHint:
      'Du plus cher au moins cher, ventilé par type de produit. Une ligne ouvre le détail des coûts du projet.',
    byProjectEmpty: {
      title: 'Aucun coût par projet',
      description: 'Aucune consommation mesurée sur cette période.',
    },
    noProject: 'Sans projet',
    unpriced: {
      count_one: '{{count}} produit sans prix',
      count_other: '{{count}} produits sans prix',
      note_one:
        '{{count}} ligne compte à zéro des produits absents de la grille de prix : le montant réel est plus élevé.',
      note_other:
        '{{count}} lignes comptent à zéro des produits absents de la grille de prix : le montant réel est plus élevé.',
    },
    linesTitle: 'Consommation',
    linesSubtitle: 'Regroupée par type de produit et référence (type d’instance, egress, journaux).',
    projectTitle: 'Coûts — {{project}}',
    projectSubtitle: 'Consommation et coûts de ce projet.',
    projectCrumb: 'Coûts du projet',
    backToTenant: 'Voir tout le tenant',
    productTypes: { INSTANCE: 'Instances', EGRESS: 'Trafic sortant', LOGS: 'Journaux', SUPPORT: 'Support' },
    quantity: { minutes: '{{minutes}} min ({{hours}} h)', gigabytes: '{{value}} Go' },
    columns: {
      project: 'Projet',
      cost: 'Coût',
      breakdown: 'Répartition',
      unpriced: 'Non tarifé',
      type: 'Produit',
      reference: 'Référence',
      quantity: 'Quantité',
      jobs: 'Jobs',
      last: 'Dernière mesure',
      utilization: 'Utilisation',
    },
    utilization: {
      label: 'Utilisation CPU : {{ratio}}',
      unmeasured: 'non mesurée',
      unmeasuredLabel: 'Utilisation CPU non mesurée',
      cpu: 'CPU : {{used}} vCPU·h utilisés sur {{allocated}} alloués ({{ratio}})',
      cpuUnmeasured: 'CPU : {{allocated}} vCPU·h alloués, consommation non mesurée',
      memory: 'Mémoire : {{used}} Gio·h utilisés sur {{allocated}} alloués ({{ratio}})',
      memoryUnmeasured: 'Mémoire : {{allocated}} Gio·h alloués',
      coverage: 'Calculée sur {{coverage}} de l’alloué : les charges dont la consommation a été relevée.',
      low: 'Moins de 30 % des vCPU réservés sont utilisés : la réservation est surdimensionnée.',
      method: 'Utilisation = CPU consommé ÷ CPU réservé, charge par charge (runs, espaces, endpoints).',
    },
    emptyLines: { title: 'Aucune consommation', description: 'Rien n’a été mesuré sur cette période.' },
    chart: {
      perDay: 'Coût par jour',
      perWeek: 'Coût par semaine',
      max: 'maximum {{value}}',
      week: 'semaine du {{date}}',
      label_one: 'Coût sur {{count}} période',
      label_other: 'Coût sur {{count}} périodes',
      hint: 'Flèches gauche et droite pour parcourir les périodes, Échap pour quitter.',
      table: 'Voir les valeurs',
      empty: 'Aucun coût sur cette période.',
    },
  },
  subscription: {
    title: 'Abonnement',
    subtitle:
      'Les vCPU souscrits auprès de l’éditeur, le quota CPU du namespace des runs et le pic réellement alloué sur la période.',
    vcpu: 'vCPU',
    unknown: 'Inconnu',
    subscribed: {
      label: 'vCPU souscrits',
      unknown: 'Non renseigné : GRAAL_BILLING_SUBSCRIPTION_VCPU (valeur du chart billing.subscription.vcpu).',
      hint: 'L’abonnement annuel au vCPU, facturé par l’éditeur hors de la plateforme.',
    },
    quota: {
      label: 'Quota du namespace des runs',
      source: 'ResourceQuota de {{namespace}}, clé {{key}}.',
      overSubscribed: 'Le cluster autorise plus de vCPU que l’abonnement n’en couvre.',
      forbidden:
        'graal-api n’a pas le droit de lire les ResourceQuota de {{namespace}} : ajouter get et list sur resourcequotas au Role de graal-api dans ce namespace (geste d’infrastructure).',
      absent: 'Le namespace {{namespace}} n’a aucun ResourceQuota qui borne le CPU.',
      noInfrastructure:
        'Aucune infrastructure Kubernetes de la plateforme ne lance ses charges dans {{namespace}}.',
      unavailable: 'La lecture du quota est coupée sur cette installation.',
      error:
        'Le quota de {{namespace}} n’a pas pu être lu (cluster injoignable ?) : voir les journaux de graal-api.',
    },
    peak: {
      label: 'Pic alloué sur la période',
      unknown:
        'Les usages de la période portent des types d’instance absents du catalogue : leur taille est inconnue.',
      at: 'Atteint le {{date}}.',
      meter: 'Pic rapporté aux vCPU souscrits',
      inUse: 'Alloué en ce moment : {{value}} vCPU (selon le quota).',
      overSubscribed: 'Le pic dépasse les vCPU souscrits.',
    },
    unsized_one: 'Type d’instance absent du catalogue, non compté dans le pic : {{types}}.',
    unsized_other: 'Types d’instance absents du catalogue, non comptés dans le pic : {{types}}.',
    method:
      'Le pic additionne les vCPU réservés par les runs, espaces et endpoints de tous les locataires (usages d’instance), et, si la période inclut aujourd’hui, ce que le quota compte comme alloué maintenant : une charge en cours n’a pas encore d’usage écrit.',
  },
  capacity: {
    title: 'Capacité du tenant',
    nodes: 'Nœuds',
    cpus: 'vCPU',
    cpuUnit: 'vCPU',
    memory: 'Mémoire',
    zones: 'Zones : {{zones}} — au moins {{min}} nœuds.',
  },
  quotas: {
    title: 'Quotas et capacité',
    subtitle: 'Les limites du tenant et ce qu’il en consomme.',
    listTitle: 'Quotas',
    enforcement:
      'Chaque lancement est contrôlé : un run qui dépasserait un plafond est refusé, avec le plafond et l’usage. Les vCPU et la mémoire en cours comptent les runs et les espaces de travail en marche. Seul l’administrateur de la plateforme relève un plafond ; « Demander plus » lui ouvre un ticket.',
    searchPlaceholder: 'Rechercher un quota…',
    meterLabel: 'Consommation du quota {{name}}',
    columns: { name: 'Quota', description: 'Ce qu’il limite', scope: 'Portée', usage: 'Consommé / limite' },
    scopes: {
      default: 'Défaut de la plateforme',
      sku: 'Offre',
      tenant: 'Fixé par la plateforme',
    },
    scopeHints: {
      default: 'Le plafond par défaut de toute organisation.',
      sku: 'Le plafond de l’offre souscrite.',
      tenant: 'Réglé pour cette organisation par l’administrateur de la plateforme.',
    },
    fields: { level: 'Niveau', sku: 'Offre' },
    empty: { title: 'Aucun quota', description: 'Ce tenant n’a pas de limite déclarée.' },
    errors: { integer: 'Un nombre entier.', aboveLimit: 'Au-dessus de la limite actuelle ({{limit}}).' },
    detail: {
      tabs: { summary: 'Résumé', increase: 'Demander plus' },
      pending: 'Demandes en cours',
      noPending: 'Aucune demande d’augmentation ouverte.',
    },
    increase: {
      hint: 'Une demande ouvre un ticket de support : l’exploitant relève la limite après vérification.',
      newValue: 'Nouvelle limite',
      newValueHelp: 'Limite actuelle : {{limit}}.',
      reason: 'Pourquoi ?',
      reasonHelp: 'Le besoin, sa durée, ce qui se passe sans : l’exploitant décide sur cette base.',
      submit: 'Envoyer la demande',
      sent: 'Demande envoyée : un ticket a été ouvert.',
      ticketTitle: 'Relever le quota « {{name}} » à {{value}}',
    },
  },
  quotaExceeded: {
    full: 'Quota dépassé : {{label}} ({{key}}) — plafond {{max}}, déjà utilisé {{current}}, demandé {{requested}}. Arrêtez un espace ou un run, choisissez un type d’instance plus petit, ou demandez plus de quota.',
    short:
      'Quota dépassé : {{label}} ({{key}}). Arrêtez un espace ou un run, choisissez un type d’instance plus petit, ou demandez plus de quota.',
    unknownKey: 'quota inconnu',
    keys: {
      'runs.cpu.max': 'vCPU en cours dans l’organisation',
      'runs.memory.max': 'mémoire en cours dans l’organisation',
      'job.cpu.max': 'vCPU d’un seul run ou espace',
      'job.memory.max': 'mémoire d’un seul run ou espace',
    },
  },
  tickets: {
    title: 'Support',
    subtitle: 'Les tickets du tenant : incidents et demandes de quota.',
    searchPlaceholder: 'Rechercher un ticket…',
    new: 'Nouveau ticket',
    newTitle: 'Nouveau ticket',
    newSubtitle: 'Décrivez l’incident : ce qui se passe, depuis quand, ce que vous avez essayé.',
    submit: 'Ouvrir le ticket',
    created: 'Ticket ouvert.',
    columns: { title: 'Ticket', kind: 'Nature', status: 'Statut', created: 'Ouvert' },
    statuses: { OPEN: 'Ouvert', CLOSED: 'Clos' },
    kinds: {
      issue: 'Incident',
      quota: 'Quota {{key}} → {{value}}',
      subscription: 'Souscription',
    },
    severities: { p1_minimal: 'Mineur', p2_moderate: 'Modéré', p3_critical: 'Critique' },
    resources: {
      job: 'Job',
      workflow: 'Workflow',
      workspace: 'Espace de travail',
      endpoint: 'Endpoint de serving',
      datawarehouse: 'Entrepôt SQL',
      bucket: 'Bucket',
      infrastructure: 'Infrastructure',
    },
    fields: {
      title: 'Titre',
      titlePlaceholder: 'Le run de conso-regionale reste en file',
      severity: 'Gravité',
      severityHelp: 'Critique : la production est arrêtée. Modéré : dégradée. Mineur : gêne.',
      resourceType: 'Ressource concernée',
      resourceId: 'Identifiant de la ressource',
      resourceIdHelp: 'Celui de l’URL de son détail, par exemple job_8f2c.',
      description: 'Description',
    },
    empty: { title: 'Aucun ticket', description: 'Un incident ou une demande de quota ouvre un ticket.' },
    detail: {
      subtitle: 'Ticket de support du tenant.',
      tabs: { summary: 'Résumé', definition: 'Définition' },
      quota: 'Quota visé',
      close: 'Clore',
      reopen: 'Rouvrir',
      closed: 'Ticket clos.',
      reopened: 'Ticket rouvert.',
      delete: 'Supprimer',
      confirmDelete: 'Supprimer le ticket « {{name}} » ? Son historique est perdu.',
      deleted: 'Ticket « {{name}} » supprimé.',
    },
  },
  firewall: {
    title: 'Pare-feu',
    subtitle:
      'Les adresses autorisées à joindre la plateforme, et les destinations qu’elle peut joindre. Aujourd’hui, aucune règle n’est encore appliquée au trafic.',
    searchPlaceholder: 'Rechercher une règle…',
    new: 'Nouvelle règle',
    newTitle: 'Nouvelle règle',
    newSubtitle:
      'Une règle entrante autorise une adresse externe ; une règle sortante, une destination externe.',
    created: 'Règle créée.',
    columns: {
      type: 'Sens',
      source: 'Source',
      target: 'Cible',
      ports: 'Ports',
      description: 'Description',
    },
    types: { ingress: 'Entrante', egress: 'Sortante' },
    protocols: { tcp: 'TCP', udp: 'UDP', any: 'Tous' },
    targets: { console: 'Console', api: 'API', mcp: 'Serveur MCP', 'graal-run': 'Runs (graal-run)' },
    fields: {
      type: 'Sens',
      protocol: 'Protocole',
      ports: 'Ports',
      portsHelp: '443, 80,443, 8000-8080 ou * pour tous.',
      source: 'Source externe',
      sourceHelp: 'Une adresse IPv4 ou un bloc CIDR, par exemple 203.0.113.0/24.',
      target: 'Cible sur la plateforme',
      egressSource: 'Source sur la plateforme',
      egressTarget: 'Destination externe',
      egressTargetHelp: 'Une adresse IPv4 ou un bloc CIDR, par exemple 198.51.100.0/24.',
      description: 'Description',
    },
    errors: {
      ports: 'Un port de 1 à 65535, une liste séparée par des virgules, une plage croissante, ou *.',
      address: 'Une adresse IPv4 ou un bloc CIDR (a.b.c.d/n).',
      ip: 'Une adresse IPv4 (a.b.c.d).',
    },
    empty: {
      title: 'Aucune règle',
      description: 'Sans règle, rien ne distingue une adresse autorisée d’une autre.',
    },
    check: {
      title: 'Vérifier une adresse',
      hint: 'La même vérification que le backend : une règle entrante dont la source contient l’adresse l’autorise.',
      ip: 'Adresse IPv4',
      ipHelp: 'L’adresse publique d’un poste ou d’un site.',
      submit: 'Vérifier',
      granted: '{{ip}} est autorisée.',
      denied: '{{ip}} n’est autorisée par aucune règle.',
    },
    detail: {
      tabs: { summary: 'Résumé', definition: 'Définition' },
      delete: 'Supprimer',
      confirmDelete: 'Supprimer la règle « {{name}} » ?',
      deleted: 'Règle « {{name}} » supprimée.',
    },
  },
  infrastructures: {
    title: 'Infrastructures',
    subtitle: 'Les cibles où s’exécutent jobs, espaces de travail et endpoints.',
    searchPlaceholder: 'Rechercher une infrastructure…',
    new: 'Nouvelle infrastructure',
    newTitle: 'Nouvelle infrastructure',
    newSubtitle: 'Déclarer une cible d’exécution et les types d’instance qu’elle accepte.',
    inCluster: 'dans le cluster',
    connectionHint:
      'graal se connecte au cluster de la plateforme (son identité in-cluster) ou au cluster d’un kubeconfig, et n’agit qu’avec les droits que ce kubeconfig donne dans le namespace choisi. Ces droits sont vérifiés avant l’enregistrement ; le kubeconfig n’est jamais réaffiché.',
    byKubeconfig: 'par kubeconfig',
    modes: {
      inCluster: 'Cluster de la plateforme (in-cluster)',
      kubeconfig: 'Kubeconfig',
    },
    namespaceHelp:
      'Vide = namespace de la plateforme. graal n’agit qu’avec les droits que ce kubeconfig donne dans ce namespace ; il ne le crée pas.',
    namespacePlaceholder: 'namespace de la plateforme',
    kubeconfigHelp:
      'Collez le YAML ou importez le fichier (.yaml, .kubeconfig). Lu dans le navigateur, envoyé une fois, jamais réaffiché.',
    kubeconfigKeep: 'Laisser vide pour garder le kubeconfig enregistré (il n’est jamais réaffiché).',
    kubeconfigKeepPlaceholder: 'Kubeconfig enregistré — laisser vide pour le garder',
    contextHelp: 'Facultatif : le contexte du kubeconfig à utiliser ; vide = son contexte courant.',
    platformNamespace: 'namespace de la plateforme',
    currentContext: 'contexte courant du kubeconfig',
    created: 'Infrastructure « {{name}} » créée.',
    publicIps: 'Adresses publiques',
    noInstanceType: 'Aucun type d’instance autorisé.',
    columns: {
      name: 'Nom',
      target: 'Cible',
      location: 'Emplacement',
      instanceTypes: 'Types d’instance',
    },
    fields: {
      name: 'Nom',
      location: 'Emplacement',
      country: 'Pays',
      description: 'Description',
      mode: 'Connexion',
      namespace: 'Namespace',
      kubeconfig: 'Kubeconfig',
      context: 'Contexte',
    },
    services: {
      scheduler: 'Planificateur',
      logging: 'Journaux',
      workflow: 'Workflows',
      nvidia: 'GPU NVIDIA',
      runtimes: 'Runtimes distribués',
    },
    errors: {
      noInstanceType: 'Autorisez au moins un type d’instance.',
      namespace:
        'Namespace invalide : lettres minuscules, chiffres et « - », commençant et finissant par une lettre ou un chiffre, 63 caractères au plus.',
    },
    empty: {
      title: 'Aucune infrastructure',
      description: 'Sans infrastructure, aucun run ne peut être planifié.',
    },
    detail: {
      tabs: {
        summary: 'Résumé',
        capacity: 'Capacité',
        services: 'Services',
        instanceTypes: 'Types d’instance',
        check: 'Droits Kubernetes',
        definition: 'Définition',
        access: 'Accès',
      },
      accessHint: 'Qui peut lire, modifier ou exécuter sur cette infrastructure.',
      delete: 'Supprimer',
      confirmDelete: 'Supprimer l’infrastructure « {{name}} » ? Les runs qui la visent échoueront.',
      deleted: 'Infrastructure « {{name}} » supprimée.',
    },
    trust: {
      label: 'Fiabilité',
      trusted: 'Fiable',
      untrusted: 'Cluster non fiable',
      help: 'Un administrateur de locataire a écrit la cible Kubernetes de cette infrastructure (kubeconfig, namespace, contexte ou mode) : le cluster est contrôlé par le locataire. graal ne lui remet aucun secret de la plateforme ni du locataire (identité du job, clés du stockage objet, identifiants du registre d’images, mots de passe) et y refuse espaces de travail, endpoints, streams et entrepôts de données.',
      warning:
        'Vous n’êtes pas administrateur de la plateforme : poser un kubeconfig, un namespace ou un contexte, ou changer le mode de connexion, rendra cette infrastructure non fiable. graal ne lui remettra alors aucun secret (identité du job, clés du stockage objet, identifiants du registre d’images, mots de passe) et y refusera espaces de travail, endpoints, streams et entrepôts de données.',
    },
    unreachableReasons: {
      unreachable: 'apiserver injoignable',
      tls: 'certificat TLS refusé',
      http: 'réponse HTTP {{code}}',
      serverNotAllowed: 'adresse non autorisée',
      invalidKubeconfig: 'kubeconfig invalide',
    },
    serverErrors: {
      insufficientRights:
        'Ce kubeconfig n’a pas tous les droits requis dans le namespace « {{namespace}} ». Droits manquants : {{rights}}.',
      insufficientRightsUnknown:
        'Ce kubeconfig n’a pas tous les droits requis dans le namespace. « Vérifier les droits » sur la fiche les détaille.',
      unreachable: 'L’apiserver {{server}} n’est pas utilisable avec ce kubeconfig : {{cause}}.',
      unreachableNoServer: 'Ce kubeconfig n’est pas utilisable : {{cause}}.',
      unreachableUnknown:
        'L’apiserver n’est pas joignable avec ce kubeconfig, ou il refuse ses identifiants.',
      platformAdminOnly:
        'Seul l’administrateur de la plateforme peut poser un kubeconfig sur une infrastructure partagée, ou pointer l’identité de graal vers un autre namespace.',
      tenantAdminOnly: 'Seul un administrateur du locataire (ou de la plateforme) peut poser un kubeconfig.',
      serverNotAllowed:
        'Cet apiserver n’est pas autorisé pour un administrateur de locataire : seul un apiserver public, ou autorisé par la plateforme, est accepté.',
      kubeconfigRequired:
        'Les identifiants de cette infrastructure ont été posés par la plateforme : pour changer seul le namespace ou le contexte, joignez votre propre kubeconfig à la même modification.',
      invalidKubeconfig:
        'Kubeconfig refusé : YAML illisible, contexte introuvable, ou mécanisme interdit (exec-plugin, fichier local).',
      notKubernetes: 'Cette infrastructure n’a pas de cible Kubernetes : aucun droit à vérifier.',
    },
    check: {
      hint: 'Demande à l’apiserver, avec le kubeconfig enregistré, chaque droit dont graal a besoin dans le namespace effectif.',
      run: 'Vérifier les droits',
      running: 'Vérification…',
      failed: 'Vérification impossible',
      idle: 'Aucune vérification lancée.',
      status: 'Verdict',
      ready: 'Prêt',
      insufficient: 'Droits insuffisants',
      server: 'Apiserver',
      namespace: 'Namespace effectif',
      rightsTitle: 'Droits vérifiés',
      rightsHint:
        'Un droit requis refusé empêche de lancer des runs ; un droit indicatif ne gêne que les fonctions qui en dépendent.',
      noRights: 'Aucun droit rendu par le serveur.',
      required: 'Requis',
      optional: 'Indicatif',
      allowed: 'Accordé',
      denied: 'Refusé',
      platformOnly:
        'Seul l’administrateur de la plateforme vérifie les droits d’une infrastructure partagée ou de l’identité de graal (modes in-cluster et frompod).',
      columns: {
        resource: 'Ressource',
        verb: 'Verbe',
        required: 'Requis',
        allowed: 'Autorisé',
        reason: 'Raison',
      },
    },
  },
  instanceTypes: {
    title: 'Types d’instance',
    subtitle: 'Les gabarits de ressources qu’un job, un espace de travail ou un endpoint demande.',
    searchPlaceholder: 'Rechercher un type…',
    columns: {
      id: 'Type',
      family: 'Famille',
      cpus: 'vCPU',
      memory: 'Mémoire',
      storage: 'Stockage',
      gpus: 'GPU',
      accepted: 'Infrastructures',
    },
    fields: { generation: 'Génération' },
    detail: {
      tabs: { summary: 'Résumé', infrastructures: 'Infrastructures' },
      noInfrastructure: 'Aucune infrastructure du tenant n’accepte ce type.',
    },
  },
  runtimes: {
    title: 'Runtimes',
    subtitle:
      'Les environnements d’exécution des jobs : le catalogue de la plateforme, et vos runtimes construits sur une de ses images de base.',
    searchPlaceholder: 'Rechercher un runtime…',
    new: 'Nouveau runtime',
    newTitle: 'Nouveau runtime',
    newSubtitle:
      'Une image de base de la plateforme plus vos paquets pip : construite sans root dans votre infrastructure, poussée dans le registre de votre organisation.',
    created: 'Runtime « {{name}} » créé.',
    columns: {
      name: 'Runtime',
      technicalName: 'Nom technique',
      status: 'Statut',
      enabled: 'Activé',
      scope: 'Origine',
    },
    scopes: { platform: 'Plateforme', tenant: 'Organisation' },
    fields: {
      name: 'Nom',
      technicalName: 'Nom technique',
      technicalNameHelp: 'Minuscules, chiffres, . _ -.',
      description: 'Description',
      base: 'Image de base',
      baseHelp: 'Une image de la plateforme : la couche de vos paquets s’y ajoute, sans Dockerfile.',
      bases: { python: 'Python (graal-runtime/python)' },
      project: 'Projet',
      projectHelp: 'Les constructions tournent et sont comptées dans ce projet.',
      dockerfile: 'Dockerfile',
    },
    errors: {
      imageName: 'Minuscules et chiffres, séparés par . _ ou -.',
      dependencies:
        'Une ligne par paquet, « nom==version » avec une version EXACTE (ex. pandas==2.2.3) ; ni option, ni URL, ni doublon.',
    },
    empty: { title: 'Aucun runtime', description: 'Les runtimes du tenant s’afficheront ici.' },
    versions: {
      title: 'Versions',
      empty: 'Aucune version.',
      new: 'Nouvelle version',
      newTitle: 'Nouvelle version',
      newSubtitle:
        'Les paquets pip de cette version, épinglés : une autre liste est une autre version. Seules les roues (wheels) sont installées.',
      created: 'Version « {{name}} » créée.',
      columns: { name: 'Version', image: 'Image', built: 'Image construite' },
      fields: {
        name: 'Nom',
        technicalName: 'Nom technique',
        description: 'Description',
        dependencies: 'Dépendances',
        dependenciesHelp: 'Une ligne par paquet : nom==version.',
      },
      image: 'Image construite',
      imageHelp: 'Épinglée par digest : à mettre dans « Image Docker » (docker_image) d’un job.',
      notBuilt: 'Pas encore construite.',
      build: 'Construction',
    },
    dependencies: {
      title: 'Dépendances',
      reference: 'Paquet',
      version: 'Version',
      empty: 'Aucune dépendance déclarée.',
    },
    builds: {
      title: 'Constructions',
      warning:
        'Une construction installe les paquets de la version dans l’image de base, sans root, puis pousse l’image dans le registre de votre organisation. Le catalogue de la plateforme se construit par sa CI.',
      start: 'Construire',
      started: 'Construction {{name}} lancée.',
      empty: 'Aucune construction.',
      logs: 'Journal',
      noLogs: 'Aucune ligne de journal.',
      columns: { name: 'Construction', initiator: 'Demandée par' },
      newTitle: 'Construire une version',
      newSubtitle:
        'Le Job tourne dans l’infrastructure choisie (une infrastructure de la plateforme : l’identifiant de poussée n’en sort jamais).',
      fields: { version: 'Version', infrastructure: 'Infrastructure' },
      image: 'Image produite',
      noImage: 'Disponible à la fin de la construction.',
      catalog: 'Le catalogue de la plateforme se construit par sa CI, pas depuis la console.',
    },
    detail: {
      tabs: { summary: 'Résumé', versions: 'Versions', builds: 'Constructions' },
      delete: 'Supprimer',
      confirmDelete: 'Supprimer le runtime « {{name}} » ? Les jobs qui l’utilisent ne démarreront plus.',
      deleted: 'Runtime « {{name}} » supprimé.',
    },
  },
  devices: {
    title: 'Appareils',
    subtitle:
      'Les sites edge rattachés au tenant : des boîtiers au plus près des capteurs, qui exécutent les jobs sur place.',
    searchPlaceholder: 'Rechercher un appareil…',
    columns: {
      presence: 'État',
      name: 'Appareil',
      location: 'Emplacement',
      target: 'Cible',
      mode: 'Mode',
      agent: 'Version de l’agent',
      lastSeen: 'Dernier contact',
    },
    presence: { online: 'En ligne', offline: 'Hors ligne', untracked: 'Non suivi' },
    modes: { agent: 'Agent (sortie 443)', kubeconfig: 'Kubeconfig (réseau privé)' },
    filters: { label: 'Présence', all: 'Tous', online: 'En ligne', offline: 'Hors ligne' },
    fields: {
      name: 'Nom',
      location: 'Emplacement',
      country: 'Pays',
      description: 'Description',
      instanceTypes: 'Types d’instance',
    },
    empty: {
      title: 'Aucun appareil',
      description:
        'Un site apparaît ici dès que la commande d’installation a été collée sur son boîtier : « Ajouter un site edge ».',
    },
    edge: {
      action: 'Ajouter un site edge',
      title: 'Ajouter un site edge',
      subtitle:
        'Nommez le site : la console donne la commande à coller sur le boîtier Linux. Il se déclare tout seul, sans rien ouvrir en entrée.',
      name: 'Nom du site',
      nameHelp: 'Ce qui permettra de reconnaître le site dans la console.',
      namePlaceholder: 'Poste source de Saumur',
      location: 'Lieu',
      locationPlaceholder: 'Saumur, armoire A',
      submit: 'Obtenir la commande',
      created: 'Commande d’installation prête.',
      commandTitle: 'Collez cette commande sur le boîtier Linux du site',
      copy: 'Copier la commande',
      copiedLabel: 'Copiée',
      copied: 'Commande copiée.',
      noCommand:
        'La plateforme n’a pas d’adresse d’API configurée : elle ne peut pas composer la commande. Code d’enrôlement : {{code}} — à transmettre à l’administrateur de la plateforme.',
      expiry: 'Code {{code}}, à usage unique, valable 24 h (jusqu’au {{date}}).',
      once: 'Affiché une seule fois : quitter cet écran le perd.',
      network:
        'Seule exigence réseau : une sortie 443 vers {{host}}, proxy accepté. Rien à ouvrir en entrée.',
      waiting: 'En attente de « {{name}} »…',
      waitingHint:
        'Collez la commande sur le boîtier : cette page passe d’elle-même à la suite dès que le site se déclare.',
      expired: 'Le code a expiré sans qu’aucun site ne se déclare : ajoutez le site de nouveau.',
      arrived: '« {{name}} » est en ligne.',
      arrivedHint: 'Le code est consommé : la commande ne sert plus.',
      view: 'Voir le site',
      another: 'Ajouter un autre site',
      agentOff:
        'Le mode agent n’est pas activé sur cette plateforme : aucun code n’a été émis. Le certificat du mode kubeconfig, ci-dessous, a été émis à la place.',
    },
    kubeconfig: {
      title: 'Mode kubeconfig (réseau privé)',
      hint: 'L’ancien parcours : un certificat PKCS#12, la plateforme pilote le cluster du site par un réseau privé.',
    },
    trust: {
      label: 'Appareil fiable',
      help: 'Un appareil fiable peut recevoir l’identité des jobs qu’il exécute (accès au catalogue, aux bibliothèques et aux données du projet sous cette identité). Les clés de stockage du locataire n’y partent jamais. Non fiable : un job qui exige une identité y est refusé (DEVICE_NOT_TRUSTED).',
      agentNote:
        'Mode agent (v1) : aucune identité ne part encore vers le site ; ce réglage prendra effet avec le relais d’identité de l’agent.',
      isTrusted: 'Cet appareil est fiable.',
      isNotTrusted: 'Cet appareil n’est pas fiable.',
      reserved: 'Seul l’administrateur de la plateforme peut changer ce réglage.',
      granted: 'Appareil marqué fiable.',
      revoked: 'Appareil retiré des appareils fiables.',
      failed: 'Le réglage n’a pas été changé : {{detail}}',
      confirmTitle: 'Marquer « {{name}} » comme fiable ?',
      confirm:
        'Les jobs qui s’y exécutent recevront leur identité : quiconque contrôle ce boîtier pourra agir sous cette identité tant que le run dure. À réserver à un site que votre organisation administre.',
      confirmLabel: 'Marquer fiable',
    },
    delivery: {
      title: 'Livraison au site edge',
      at: '{{state}} {{time}}',
      steps: {
        queued: 'En file',
        published: 'Envoyé',
        accepted: 'Accepté',
        running: 'En cours',
        finished: 'Terminé',
      },
      states: {
        queued: 'En file',
        published: 'Envoyé',
        accepted: 'Accepté',
        rejected: 'Refusé',
        expired: 'Expiré',
        withdrawn: 'Retiré',
      },
      stepState: { done: 'fait', current: 'en cours', todo: 'à venir', failed: 'échec' },
      failure: {
        rejected: 'Le site a refusé la commande : elle sort de sa politique locale.',
        expired:
          'La commande n’a pas été livrée avant son échéance (24 h par défaut) : le site est resté hors ligne. Le run est en échec, il ne partira pas.',
        withdrawn: 'La commande a été retirée de la file avant l’envoi (run annulé).',
      },
      otherReason: 'Raison :',
      reasons: {
        RUN_EXPIRED: 'Échéance passée avant la livraison.',
        RUN_REJECTED: 'Refusé par le site.',
        RUN_KILLED: 'Run annulé.',
        POLICY_REGISTRY_DENIED: 'Registre d’images non autorisé par le site.',
        REGISTRY_NOT_ALLOWED: 'Registre d’images non autorisé.',
        POLICY_DIGEST_REQUIRED: 'Le site exige une image désignée par son empreinte (@sha256).',
        POLICY_TYPE_DENIED: 'Type de job non autorisé par le site.',
        POLICY_CPU_EXCEEDED: 'CPU demandé au-delà du plafond du site.',
        POLICY_MEMORY_EXCEEDED: 'Mémoire demandée au-delà du plafond du site.',
        POLICY_TIMEOUT_EXCEEDED: 'Durée maximale au-delà du plafond du site.',
        SPEC_INVALID: 'Spécification du run illisible pour l’agent.',
        UNKNOWN_KID: 'Signature de la plateforme inconnue du site (clé non approuvée).',
        UNKNOWN_KIND: 'Commande inconnue de cette version de l’agent.',
      },
    },
    enroll: {
      subtitle:
        'La console émet un certificat ; l’appareil s’en sert pour se déclarer auprès de la plateforme.',
      description: 'Appareil prévu',
      descriptionHelp: 'Ce qui permettra de reconnaître l’enrôlement : site, numéro de série…',
      descriptionPlaceholder: 'Poste source de Cholet, armoire B',
      submit: 'Émettre le certificat',
      created: 'Certificat émis.',
      issued: 'Certificat de l’appareil',
      issuedHint:
        'Magasin PKCS#12 encodé en base64, à usage unique et valable 7 jours : déposez-le sur l’appareil, son agent le présente à la plateforme.',
      copy: 'Copier le certificat',
      download: 'Télécharger (.p12)',
      command:
        'Sur l’appareil, l’agent graal (devices/agent) s’enrôle avec ce fichier, puis déclare son cluster ; l’enrôlement est consommé :',
      pending: 'Enrôlements en attente',
      pendingHint: 'Certificats émis qu’aucun appareil n’a encore utilisés.',
      none: 'Aucun enrôlement en attente.',
      revoke: 'Révoquer l’enrôlement',
      confirmRevoke: 'Révoquer l’enrôlement « {{name}} » ? Son certificat ne permettra plus de s’enrôler.',
      revoked: 'Enrôlement révoqué.',
      columns: { description: 'Appareil prévu' },
    },
    detail: {
      tabs: { summary: 'Résumé', definition: 'Définition', access: 'Accès' },
      delete: 'Supprimer',
      confirmDelete: 'Supprimer l’appareil « {{name}} » ? Il devra être enrôlé de nouveau.',
      deleted: 'Appareil « {{name}} » supprimé.',
    },
  },
  bridges: {
    title: 'Bridges',
    subtitle:
      'Le pont YARN de graal : un client Hadoop ou Spark non modifié y soumet ses applications comme à un ResourceManager, et chacune devient un run du projet du pont. Joignable seulement si la plateforme a déployé le composant YARN.',
    new: 'Nouveau bridge',
    newTitle: 'Nouveau bridge',
    newSubtitle:
      'Le projet, l’identité, l’infrastructure et le bucket sous lesquels les applications YARN de vos clients Hadoop s’exécuteront.',
    created: 'Bridge créé.',
    columns: { description: 'Bridge', type: 'Type', owner: 'Propriétaire' },
    types: { yarn: 'YARN (Hadoop)' },
    fields: {
      type: 'Type',
      description: 'Description',
      project: 'Projet',
      projectHelp:
        'Les applications deviennent des runs du job « yarn-<bridge> » de ce projet. Il faut pouvoir modifier ce projet.',
      identity: 'Identité',
      identityHelp:
        'Les applications s’exécutent sous cette identité, avec ses seuls droits. Il faut pouvoir l’utiliser (/identity/use).',
      infrastructure: 'Infrastructure',
      infrastructureHelp: 'Le cluster où tournent les pods des applications.',
      bucket: 'Bucket',
      bucketHelp:
        'Le bucket S3 du staging et des données (fs.defaultFS = s3a://<nom>). Il doit porter le label graal.systems/project du projet, et l’identité doit pouvoir le lire et l’écrire.',
      mode: 'Mode',
      modeHelp:
        'Réparti : un pod par conteneur YARN (MapReduce et Spark). Local : un seul pod qui rejoue le job (MapReduce seulement).',
      instanceType: 'Type d’instance',
      instanceTypeHelp: 'La taille du pod principal de chaque application.',
      s3Endpoint: 'Endpoint S3',
      s3EndpointHelp:
        'L’endpoint S3 tel que le joint le poste du client, écrit dans core-site.xml (fs.s3a.endpoint).',
    },
    modes: { default: 'Défaut de la plateforme', distributed: 'Réparti', local: 'Local' },
    instanceTypes: { auto: 'Automatique (le plus petit sans GPU)' },
    errors: {
      endpoint: 'Une URL en http:// ou https://.',
      identityNotUsable: 'Vous ne pouvez pas utiliser cette identité : /identity/use est requis.',
    },
    empty: {
      title: 'Aucun bridge',
      description: 'Un bridge laisse un client Hadoop ou Spark existant soumettre ses applications à graal.',
    },
    detail: {
      tabs: {
        summary: 'Résumé',
        definition: 'Définition',
        configuration: 'Fichiers client',
        access: 'Accès',
      },
      status:
        'Un client Hadoop 3.x ou Spark non modifié soumet à ce bridge avec les fichiers de l’onglet « Fichiers client ». Le bridge n’est joignable que si la plateforme a déployé le composant YARN ; sinon le client échoue à la connexion.',
      delete: 'Supprimer',
      confirmDelete: 'Supprimer le bridge « {{name}} » ? Ses jetons sont révoqués.',
      deleted: 'Bridge « {{name}} » supprimé.',
    },
    files: {
      title: 'Fichiers du client Hadoop',
      download: 'Télécharger',
      tokenHint:
        'Fichier binaire à garder secret : il agit sous l’identité du bridge. Sa durée de vie est limitée par la plateforme : le télécharger à nouveau à échéance.',
      tokenMissing:
        'Fichier de jetons non rendu : il faut pouvoir modifier ce bridge et utiliser son identité. Sans lui, le bridge refuse le client.',
      tokenInvalid: 'Le fichier de jetons reçu est illisible (base64 invalide).',
      s3Hint:
        'Clés S3 du bucket du bridge (variables AWS_*, lues par S3A dans l’environnement du client). Elles restent valides après l’expiration du jeton et le retrait de vos droits : à garder secret, jamais dans un fichier XML.',
      s3Missing:
        'Clés S3 non rendues : l’identité du bridge doit pouvoir lire et modifier son bucket. Sans elles, le client pose ses propres clés.',
      usageTitle: 'Utilisation',
      usage:
        'Posez les trois fichiers XML dans le dossier désigné par HADOOP_CONF_DIR et le fichier de jetons dans HADOOP_TOKEN_FILE_LOCATION, puis soumettez comme à un ResourceManager :',
      usageS3:
        'Le client lit et écrit le bucket avec les clés de graal-s3.env, chargées dans son environnement ; à défaut, avec ses propres clés (fs.s3a.access.key et fs.s3a.secret.key).',
      empty: 'Aucun fichier rendu pour ce bridge.',
    },
  },
  streams: {
    title: 'Streams',
    subtitle: 'Les flux d’évènements que des jobs produisent et consomment.',
    searchPlaceholder: 'Rechercher un stream…',
    new: 'Nouveau stream',
    newTitle: 'Nouveau stream',
    newSubtitle:
      'Un Kafka ou un Pulsar que graal déploie sur l’infrastructure du tenant, ou un flux existant déclaré.',
    created: 'Stream « {{name}} » créé.',
    columns: { name: 'Stream', engine: 'Moteur', status: 'État', description: 'Description' },
    engines: { kafka: 'Kafka géré', pulsar: 'Pulsar géré', external: 'Externe' },
    fields: {
      name: 'Nom',
      nameHelp: 'Minuscules, chiffres, . _ -.',
      engine: 'Moteur',
      engineHelp:
        'Géré : un nœud Kafka (KRaft) ou un Pulsar standalone déployé par graal, joignable par les runs du tenant. Externe : un flux qui existe déjà.',
      infrastructure: 'Infrastructure',
      infrastructureHelp: 'Le cluster où le moteur tourne.',
      identity: 'Identité',
      identityHelp: 'Compte machine auquel le flux est rattaché (obligatoire).',
      instanceType: 'Type d’instance',
      instanceTypeHelp: 'Un seul nœud, plafonné à 2 vCPU et 4 Gio.',
      conf: 'Configuration Kafka',
      confHelp:
        'Une ligne clé=valeur par réglage du broker. Écoutes, quorum, répertoires et mémoire sont posés par la plateforme.',
      username: 'Identifiant',
      password: 'Mot de passe',
      passwordHelp: 'Scellé dans le coffre du tenant ; il ne se relit pas.',
      description: 'Description',
    },
    errors: {
      name: 'Minuscules, chiffres, . _ -, 249 caractères au plus.',
      conf: 'Une ligne clé=valeur par réglage, la clé en minuscules (log.retention.hours).',
    },
    refusals: {
      identity: 'Choisissez l’identité du flux : le serveur l’exige.',
      reservedKey: 'La clé « {{key}} » est posée par la plateforme : retirez-la de la configuration.',
      invalidKey: 'La clé « {{key}} » n’est pas une clé de configuration Kafka.',
      invalidValue: 'La valeur de « {{key}} » est trop longue.',
      tooManyKeys: 'Trop de réglages : 64 au plus.',
      pulsarConf: 'Un Pulsar géré ne prend pas de configuration.',
      instanceType: 'Ce type d’instance n’existe pas sur ce tenant.',
      invalid: 'Ce flux a été refusé par le serveur.',
    },
    empty: { title: 'Aucun stream', description: 'Les flux d’évènements du tenant s’afficheront ici.' },
    detail: {
      tabs: { summary: 'Résumé', definition: 'Définition', access: 'Accès' },
      version: 'Version du moteur',
      address: 'Adresse d’amorçage',
      owner: 'Propriétaire',
      managedNote:
        'Un seul nœud, joignable par les runs de ce tenant à l’adresse d’amorçage. Ses données ne survivent pas à un arrêt du flux.',
      delete: 'Supprimer',
      confirmDelete: 'Supprimer le stream « {{name}} » ? Ses évènements sont perdus.',
      deleted: 'Stream « {{name}} » supprimé.',
    },
  },
  workspaces: {
    title: 'Espaces de travail',
    subtitle:
      'Jupyter, VS Code, RStudio, Grafana, Superset, Metabase et Zeppelin, ouverts par la passerelle d’applications.',
    kindSubtitle: {
      jupyter: 'Carnets Jupyter du tenant, ouverts par la passerelle d’applications.',
      vscode: 'Éditeurs VS Code du tenant, ouverts par la passerelle d’applications.',
      rstudio: 'Sessions RStudio du tenant, ouvertes par la passerelle d’applications.',
    },
    searchPlaceholder: 'Rechercher un espace…',
    new: 'Nouvel espace',
    newTitle: 'Nouvel espace de travail',
    newSubtitle:
      'Un éditeur interactif sur l’infrastructure du tenant, avec le bucket et Trino déjà branchés.',
    created: 'Espace « {{name}} » créé : il démarre.',
    open: 'Ouvrir',
    openFailed: 'Impossible d’ouvrir l’espace',
    power: {
      start: 'Démarrer',
      stop: 'Arrêter',
      starting: 'Espace « {{name}} » : démarrage demandé.',
      stopping: 'Espace « {{name}} » : arrêt demandé.',
      startFailed: 'L’espace n’a pas démarré',
      stopFailed: 'L’espace ne s’est pas arrêté',
      forbidden: 'Il faut le droit de modifier cet espace, ou son projet.',
      notFound: 'Cet espace de travail n’existe plus.',
    },
    starting: 'Démarrage…',
    notRunning: 'Arrêté',
    soon: '{{type}} — bientôt',
    types: {
      jupyter: 'Jupyter',
      vscode: 'VS Code',
      rstudio: 'RStudio',
      grafana: 'Grafana',
      superset: 'Superset',
      metabase: 'Metabase',
      zeppelin: 'Zeppelin',
      'sas-studio': 'SAS Studio',
    },
    columns: {
      name: 'Espace',
      type: 'Type',
      status: 'Statut',
      instanceType: 'Type d’instance',
      owner: 'Propriétaire',
      project: 'Projet',
    },
    fields: {
      type: 'Type',
      name: 'Nom',
      instanceType: 'Type d’instance',
      infrastructure: 'Infrastructure',
      infrastructureHelp: 'Seule infrastructure du tenant : elle est prise d’office.',
      project: 'Projet',
      projectHelp: 'Qui peut modifier le projet peut ouvrir, modifier, démarrer et arrêter l’espace.',
      noProject: 'Aucun projet',
      description: 'Description',
      version: 'Version',
      publicUrl: 'Adresse',
    },
    files: {
      hint: 'Déposer des fichiers dans l’espace (carnets, jeux d’essai), sous le dossier choisi.',
      path: 'Dossier',
      input: 'Fichiers à déposer',
      upload: 'Déposer des fichiers',
      invalidPath: 'Un chemin absolu, sans « .. ».',
      uploaded_one: '{{count}} fichier déposé dans {{path}}.',
      uploaded_other: '{{count}} fichiers déposés dans {{path}}.',
      failed: 'Dépôt refusé : {{detail}}',
    },
    empty: {
      title: 'Aucun espace de travail',
      description: 'Créez un Jupyter ou un VS Code pour explorer les données.',
    },
    detail: {
      tabs: {
        summary: 'Résumé',
        logs: 'Journal',
        files: 'Fichiers',
        definition: 'Définition',
        access: 'Accès',
      },
      gateway:
        'L’espace s’ouvre dans un nouvel onglet, derrière la passerelle d’applications, qui vous authentifie avant d’afficher l’éditeur.',
      noLogs: 'Aucune ligne de journal.',
      delete: 'Supprimer',
      confirmDelete:
        'Supprimer l’espace « {{name}} » ? Les fichiers qui n’ont pas été enregistrés ailleurs sont perdus.',
      deleted: 'Espace « {{name}} » supprimé.',
    },
  },
  endpoints: {
    title: 'Endpoints',
    subtitle: 'Les modèles servis en ligne par le projet {{project}}.',
    searchPlaceholder: 'Rechercher un endpoint…',
    new: 'Nouvel endpoint',
    newTitle: 'Nouvel endpoint',
    newSubtitle:
      'Servir une version du registre de modèles : la plateforme l’appelle pour vous, le serveur n’est jamais exposé.',
    created: 'Endpoint « {{name}} » créé : il démarre.',
    types: {
      mlflow: 'MLflow',
      sklearn: 'scikit-learn',
      xgboost: 'XGBoost',
      lightgbm: 'LightGBM',
      tfserving: 'TensorFlow Serving',
      torchserve: 'TorchServe',
    },
    columns: {
      name: 'Endpoint',
      type: 'Serveur',
      model: 'Modèle',
      replicas: 'Répliques',
      status: 'Statut',
      instanceType: 'Type d’instance',
    },
    fields: {
      name: 'Nom',
      type: 'Format du modèle',
      typeHelp:
        'Le format du modèle enregistré, qui choisit le runtime du serveur (MLflow pour un modèle journalisé par mlflow).',
      model: 'Modèle',
      modelHelp:
        'Une version du registre de modèles de ce projet. Elle ne change plus : pour en servir une autre, créez un autre endpoint.',
      replicas: 'Répliques',
      replicasHelp: 'De 0 à {{max}} ; 0 arrête l’endpoint sans le supprimer.',
      instanceType: 'Type d’instance',
      infrastructure: 'Infrastructure',
      description: 'Description',
      publicUrl: 'Adresse',
      owner: 'Propriétaire',
    },
    empty: {
      title: 'Aucun endpoint',
      description: 'Servez un modèle du registre pour l’interroger en ligne.',
    },
    test: {
      hint: 'Envoie une requête d’inférence à l’endpoint, par la plateforme : la réponse du serveur de modèle s’affiche telle quelle.',
      payload: 'Requête (JSON)',
      payloadHelp:
        'La forme MLflow : « dataframe_split » (colonnes et lignes), « dataframe_records », « instances » ou « inputs ».',
      invalid:
        'Un objet JSON avec des lignes : « dataframe_split.data », « dataframe_records », « instances » ou « inputs » non vide.',
      submit: 'Envoyer',
      response: 'Réponse',
    },
    detail: {
      tabs: {
        summary: 'Résumé',
        logs: 'Journal',
        test: 'Tester',
        definition: 'Définition',
        access: 'Accès',
      },
      noLogs: 'Aucune ligne de journal.',
      delete: 'Supprimer',
      confirmDelete:
        'Supprimer l’endpoint « {{name}} » ? Les applications qui l’appellent recevront une erreur.',
      deleted: 'Endpoint « {{name}} » supprimé.',
    },
  },
} as const;
